import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Chip,
  Paper,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { ROLE_LABELS, ROLE_COLORS } from '../../config/constants';

const ChatInput = forwardRef(({ onSend, onTyping, teamMembers }, ref) => {
  const [content, setContent] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  // @mention state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionStartIdx, setMentionStartIdx] = useState(-1);
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);

  const filteredMembers = (teamMembers || []).filter((m) =>
    m.name?.toLowerCase().includes(mentionFilter.toLowerCase())
  );

  // Close mention dropdown when no matches
  useEffect(() => {
    if (mentionOpen && filteredMembers.length === 0 && mentionFilter.length > 8) {
      setMentionOpen(false);
    }
  }, [mentionOpen, filteredMembers.length, mentionFilter]);

  const handleSend = () => {
    const trimmedContent = content.trim();
    if (!trimmedContent && !attachedFile) return;

    const attachments = attachedFile ? [attachedFile] : [];
    onSend(trimmedContent, attachments);
    setContent('');
    setAttachedFile(null);
    setMentionOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const insertMention = (member) => {
    const before = content.slice(0, mentionStartIdx);
    const after = content.slice(
      mentionStartIdx + 1 + mentionFilter.length
    );
    const newContent = `${before}@${member.name} ${after}`;
    setContent(newContent);
    setMentionOpen(false);
    setMentionFilter('');
    setMentionStartIdx(-1);
    setMentionSelectedIdx(0);

    setTimeout(() => {
      const el = inputRef.current?.querySelector('textarea, input');
      if (el) {
        el.focus();
        const pos = before.length + member.name.length + 2;
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const insertMentionAtEnd = (member) => {
    setContent((prev) => {
      const trimmed = prev.trimEnd();
      return trimmed ? `${trimmed} @${member.name} ` : `@${member.name} `;
    });
    setTimeout(() => {
      const el = inputRef.current?.querySelector('textarea, input');
      if (el) el.focus();
    }, 0);
  };

  useImperativeHandle(ref, () => ({
    insertMention: insertMentionAtEnd,
  }));

  const handleChange = (e) => {
    const val = e.target.value;
    setContent(val);

    // Notify parent about typing
    if (onTyping) onTyping();

    if (!teamMembers || teamMembers.length === 0) return;

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const lastAtIdx = textBeforeCursor.lastIndexOf('@');

    if (lastAtIdx >= 0) {
      const charBefore = lastAtIdx > 0 ? val[lastAtIdx - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || lastAtIdx === 0) {
        const typedAfterAt = textBeforeCursor.slice(lastAtIdx + 1);
        if (!typedAfterAt.includes(' ')) {
          setMentionOpen(true);
          setMentionFilter(typedAfterAt);
          setMentionStartIdx(lastAtIdx);
          setMentionSelectedIdx(0);
          return;
        }
      }
    }
    setMentionOpen(false);
  };

  const handleKeyDown = (e) => {
    if (mentionOpen && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIdx((prev) => (prev + 1) % filteredMembers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIdx((prev) => (prev - 1 + filteredMembers.length) % filteredMembers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMembers[mentionSelectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 1.5, borderTop: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', position: 'relative' }}>
      {/* @mention dropdown */}
      {mentionOpen && filteredMembers.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 12,
            right: 12,
            mb: 0.5,
            maxHeight: 200,
            overflow: 'auto',
            zIndex: 10,
            borderRadius: '10px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 4px 16px rgba(15, 23, 42, 0.1)',
          }}
        >
          <List dense disablePadding>
            {filteredMembers.map((member, idx) => (
              <ListItemButton
                key={member.name + member.role}
                selected={idx === mentionSelectedIdx}
                onClick={() => insertMention(member)}
                sx={{
                  py: 0.75,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(26, 115, 232, 0.08)',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(26, 115, 232, 0.12)',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#202124', fontSize: '0.82rem' }}>
                        {member.name}
                      </Typography>
                      <Chip
                        label={ROLE_LABELS[member.role] || member.role}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          backgroundColor: ROLE_COLORS[member.role] || '#94A3B8',
                          color: '#fff',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {/* Attached file preview */}
      {attachedFile && (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={attachedFile.name}
            size="small"
            onDelete={handleRemoveFile}
            deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            variant="outlined"
            sx={{
              maxWidth: 300,
              borderColor: '#E2E8F0',
              color: '#202124',
              '& .MuiChip-deleteIcon': { color: '#94A3B8' },
            }}
          />
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
            {(attachedFile.size / 1024).toFixed(1)} KB
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Attachment button */}
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          sx={{
            mb: 0.25,
            color: '#94A3B8',
            '&:hover': { color: '#1A73E8', backgroundColor: 'rgba(26, 115, 232, 0.08)' },
          }}
        >
          <AttachFileIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Message input */}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Type a message... (@ to mention)"
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: '#F8FAFC',
              fontSize: '0.85rem',
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#CBD5E1' },
              '&.Mui-focused fieldset': { borderColor: '#1A73E8' },
            },
          }}
        />

        {/* Send button */}
        <IconButton
          onClick={handleSend}
          disabled={!content.trim() && !attachedFile}
          sx={{
            mb: 0.25,
            backgroundColor: content.trim() || attachedFile ? '#1A73E8' : 'transparent',
            color: content.trim() || attachedFile ? '#fff' : '#94A3B8',
            width: 34,
            height: 34,
            '&:hover': {
              backgroundColor: content.trim() || attachedFile ? '#1557B0' : 'rgba(26, 115, 232, 0.08)',
            },
            '&.Mui-disabled': {
              color: '#CBD5E1',
            },
          }}
        >
          <SendRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
});

export default ChatInput;
